/* eslint-disable react/require-default-props */
import { createPortal } from 'react-dom';
import React, { useCallback, useEffect, useState } from 'react';

import AiManagementProposalAddRequirement from 'components/molecules/ai_management_proposal_add_requirement';
import AiManagementProposalRequirement from 'components/molecules/ai_management_proposal_requirement';
import ConlicitasegQuotePanel from 'components/organisms/conlicitaseg_quote_panel';
import useGuaranteeInsurance from 'hooks/useGuaranteeInsurance';
import { BiddingData } from 'store/modules/bidding_store/types';
import { proposalRequirementTarget } from 'services/ai_management_proposal';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import { useProposalDocuments } from 'hooks/useProposalDocuments';
import type {
  ProposalManualRequirementPayload,
  ProposalRequirementMomento,
} from 'services/actions/ai_management_proposal_documents';
import {
  MOMENTO_LABEL,
  PROPOSAL_REQUIREMENT_MOMENTOS,
  pendenciasAntesDoLance,
} from 'services/ai_management_proposal_documents';
import { Container } from './styles';

interface AiManagementProposalDocumentsStepProps {
  biddingId: number;
  onOpenEdital?: () => void;
  // Número do edital, que pré-preenche os modelos de declaração.
  editalNumber?: string | null;
  // Licitação, para a regra do "Fazer minha cotação" da Garantia (a mesma da licitação).
  bidding?: BiddingData | null;
}

// A etapa pode ficar para depois: quem bloqueia "Gerar proposta" é a revisão, pelo back.
const MOMENTO_ICON: Record<ProposalRequirementMomento, string> = {
  antes_do_lance: 'send',
  apos_lance: 'gavel',
};

const MOMENTO_HINT: Record<ProposalRequirementMomento, string> = {
  antes_do_lance: 'Anexados ao cadastrar a proposta no portal, antes da disputa.',
  apos_lance: 'Enviados depois do lance, quando o licitante é convocado.',
};

const AiManagementProposalDocumentsStep: React.FC<AiManagementProposalDocumentsStepProps> = ({
  biddingId,
  onOpenEdital,
  editalNumber,
  bidding,
}) => {
  const { setPendencies, trackSave, stepActions } = useProposalShell();
  const documents = useProposalDocuments(biddingId, { trackSave });
  const { state, loading, loadError } = documents;
  const guaranteeInsurance = useGuaranteeInsurance(bidding);
  const [quoteOpen, setQuoteOpen] = useState(false);
  // Contador: o erro do painel pede à Garantia que abra o "Enviar documento".
  const [sendDocumentRequest, setSendDocumentRequest] = useState(0);
  const { reload } = documents;
  const closeQuote = useCallback(() => {
    setQuoteOpen(false);
    reload();
  }, [reload]);
  const [adding, setAdding] = useState<ProposalRequirementMomento | null>(null);

  // Até carregar, a casca mantém o número que veio do back (pendencias.declaracoes).
  useEffect(() => {
    if (state) setPendencies('declaracoes', pendenciasAntesDoLance(state.requirements));
  }, [state, setPendencies]);

  const handleAdd = async (payload: ProposalManualRequirementPayload, file: File | null) => {
    const message = await documents.addRequirement(payload, file);
    if (!message) setAdding(null);
    return message;
  };

  const renderAddForm = (momento: ProposalRequirementMomento) =>
    adding === momento && (
      <AiManagementProposalAddRequirement
        defaultMomento={momento}
        onSubmit={handleAdd}
        onCancel={() => setAdding(null)}
      />
    );

  // Um botão só, ao lado do título da etapa; o formulário escolhe o momento.
  const renderAddButton = () => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
      disabled={adding !== null}
      onClick={() => setAdding('antes_do_lance')}
    >
      <i className="material-icons-outlined mr-1" aria-hidden="true" style={{ fontSize: 18 }}>
        add
      </i>
      Adicionar exigência
    </button>
  );

  if (loading && !state) {
    return <p className="text-muted mb-0">Carregando declarações e documentos…</p>;
  }

  if (loadError && !state) {
    return (
      <div className="alert alert-danger d-flex align-items-center justify-content-between mb-0">
        <span>Não foi possível carregar as declarações e os documentos.</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger ml-3"
          onClick={documents.reload}
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  if (!state) return null;

  const empty = state.requirements.length === 0;
  const contar = (status: string) =>
    state.requirements.filter((item) => item.status === status).length;
  const percentual = state.progress.total
    ? Math.round((state.progress.confirmed / state.progress.total) * 100)
    : 0;

  return (
    <Container>
      {stepActions && createPortal(renderAddButton(), stepActions)}

      {!empty && (
        <section className="progress-card">
          <div className="progress-head">
            <strong>
              {state.progress.confirmed} de {state.progress.total} exigências confirmadas
            </strong>
            <span>{percentual}%</span>
          </div>
          <div className="progress-bar-track">
            <span style={{ width: `${percentual}%` }} />
          </div>
          <div className="progress-summary">
            <span className="text-success">
              <i className="material-icons-outlined" aria-hidden="true">
                check_circle_outline
              </i>
              {contar('confirmado')} confirmadas
            </span>
            <span className="text-warning-dark">
              <i className="material-icons-outlined" aria-hidden="true">
                error_outline
              </i>
              {contar('aguardando_revisao')} aguardando revisão
            </span>
            <span className="text-danger">
              <i className="material-icons-outlined" aria-hidden="true">
                highlight_off
              </i>
              {contar('pendente')} pendentes
            </span>
          </div>
        </section>
      )}

      {adding !== null && !empty && renderAddForm(adding)}

      {!state.complete && (
        <div className="alert alert-warning mb-3" role="status">
          Faltam exigências de &ldquo;Antes do lance&rdquo;: envie, confirme ou ignore cada uma.
          Você pode deixar para depois, mas a proposta só é gerada quando todas estiverem
          resolvidas.
        </div>
      )}

      {empty && adding === null && (
        <p className="text-muted">O edital não pede declarações nem documentos para a proposta.</p>
      )}
      {empty && renderAddForm('antes_do_lance')}

      {!empty &&
        PROPOSAL_REQUIREMENT_MOMENTOS.map((momento) => {
          const items = state.requirements.filter((item) => item.momento === momento);
          if (items.length === 0) return null;

          return (
            <section key={momento} className="moment-group">
              <header>
                <h2>
                  <i className="material-icons-outlined" aria-hidden="true">
                    {MOMENTO_ICON[momento]}
                  </i>
                  {MOMENTO_LABEL[momento]}
                  <span className="count">{items.length}</span>
                </h2>
                <small>{MOMENTO_HINT[momento]}</small>
              </header>
              <div className="checks">
                {items.map((item) => (
                  <div
                    key={`${item.kind}-${item.id}`}
                    data-proposal-target={proposalRequirementTarget(item.id)}
                  >
                    <AiManagementProposalRequirement
                      requirement={item}
                      busy={documents.busy[item.id]}
                      error={documents.errors[item.id]}
                      onOpenEdital={onOpenEdital}
                      onUpload={(file) => documents.uploadFile(item, file)}
                      onUploadDeclarationFile={(file) => documents.uploadFile(item, file)}
                      onRemoveFile={(fileKey) => documents.removeFile(item, fileKey)}
                      onConfirm={() => documents.confirm(item)}
                      onIgnore={() => documents.ignore(item)}
                      onReopen={() => documents.reopen(item)}
                      onSaveDeclaration={(payload) => documents.updateDeclaration(item, payload)}
                      biddingId={biddingId}
                      editalNumber={editalNumber}
                      onUploadGuarantee={(file, modalidade) =>
                        documents.uploadFile(item, file, modalidade)
                      }
                      canQuoteGuarantee={guaranteeInsurance.showQuoteButton}
                      onOpenQuote={() => setQuoteOpen(true)}
                      sendGuaranteeDocumentRequest={sendDocumentRequest}
                    />
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      <ConlicitasegQuotePanel
        biddingId={guaranteeInsurance.biddingId || biddingId}
        tipo="participacao"
        open={quoteOpen}
        onClose={closeQuote}
        onConcluido={() => reload()}
        onSendDocument={() => {
          setQuoteOpen(false);
          setSendDocumentRequest((n) => n + 1);
        }}
      />
    </Container>
  );
};

export default AiManagementProposalDocumentsStep;
